import type { CSSProperties } from "react";

import { Icon } from "@core/components/Icon";
import { LinkBehaviour } from "@core/components/LinkBehaviour";
import { PictureBehaviour, type PictureSource } from "@core/components/PictureBehaviour";
import { DESKTOP_MEDIA_QUERY, TABLET_MEDIA_QUERY } from "@core/consts/breakpoints";
import type { SafeHtmlAttributes } from "@core/types/SafeHtmlAttributes";
import classNames from "classnames";

import { ChevronForwardIcon } from "@jti/brand-icons/chevron-forward";

import styles from "./PromoCardVertical.module.scss";

export type PromoCardVerticalProps = {
  imageSrc: string;
  imageSrcTablet?: string;
  imageSrcDesktop?: string;
  imageAlt?: string;
  title: string;
  href: string;
  contentBackgroundColor?: string;
  contentBackgroundColorTablet?: string;
  contentBackgroundColorDesktop?: string;
  nativeBehaviour?: boolean;
} & SafeHtmlAttributes<HTMLAnchorElement>;

export const PromoCardVertical = ({
  imageSrc,
  imageSrcTablet,
  imageSrcDesktop,
  imageAlt = "",
  title,
  contentBackgroundColor = "#000",
  contentBackgroundColorTablet,
  contentBackgroundColorDesktop,
  href,
  nativeBehaviour,
  ...rest
}: PromoCardVerticalProps) => {
  const sources: PictureSource[] = [
    ...(imageSrcDesktop ? [{ srcSet: imageSrcDesktop, media: DESKTOP_MEDIA_QUERY }] : []),
    ...(imageSrcTablet ? [{ srcSet: imageSrcTablet, media: TABLET_MEDIA_QUERY }] : []),
  ];

  const backgroundColorVariables: CSSProperties = {
    ...(contentBackgroundColor && { "--promo-card-bg": contentBackgroundColor }),
    ...(contentBackgroundColorTablet && { "--promo-card-bg-tablet": contentBackgroundColorTablet }),
    ...(contentBackgroundColorDesktop && { "--promo-card-bg-desktop": contentBackgroundColorDesktop }),
  };

  return (
    <LinkBehaviour
      {...rest}
      href={href}
      nativeBehaviour={nativeBehaviour}
      className={classNames("light-theme", styles.card)}
    >
      <div className={styles.imageContainer}>
        <PictureBehaviour
          className={styles.picture}
          sources={sources}
          imageProps={{ src: imageSrc, height: 248, alt: imageAlt, className: styles.image }}
          nativeBehaviour={nativeBehaviour}
        />
      </div>
      <div className={styles.contentWrapper} style={backgroundColorVariables}>
        <p className={styles.title}>{title}</p>
        <Icon icon={ChevronForwardIcon} size="sm" className={styles.icon} aria-hidden="true" />
      </div>
    </LinkBehaviour>
  );
};
